/**
 * Kai query — browser side.
 *
 * Converts a KaiQuery into the real filterState / projectFilterState (Sets, not
 * arrays) and back again. Pure: no DOM, no globals. app.js does the assigning
 * and the rendering; this module only computes.
 *
 * @module kaiQuery
 */

import {
    createEmptyKaiQuery,
    normalizeKaiQuery,
    countKaiQueryFilters,
    KAI_NONE
} from '../../utils/kai-query-shared.js';

export {
    createEmptyKaiQuery,
    createEmptyFilters,
    normalizeKaiQuery,
    validateKaiQueryAgainstVocabulary,
    buildKaiQueryReview,
    stripUnaskedNoneSentinels,
    stripUnaskedArchivedScope,
    excludeDoneForDueQueries,
    mergeKaiQuery,
    isEmptyKaiQuery,
    countKaiQueryFilters,
    nearestNames,
    KAI_NONE,
    KAI_STATUSES,
    KAI_PRIORITIES,
    KAI_DATE_PRESETS,
    KAI_LINK_TYPES,
    KAI_PROJECT_STATUSES
} from '../../utils/kai-query-shared.js';

/**
 * Build the task filterState a KaiQuery describes.
 *
 * Returns a NEW object — the caller Object.assign()s it onto the live
 * filterState so that identity is preserved for the rest of app.js.
 *
 * @param {Object} query - a validated KaiQuery
 * @returns {Object} filterState-shaped object with real Sets
 */
export function kaiQueryToFilterState(query) {
    const q = normalizeKaiQuery(query);
    const f = q.filters;

    return {
        search: f.search,
        statuses: new Set(f.statuses),
        statusExcludeMode: f.statusExcludeMode,
        priorities: new Set(f.priorities),
        priorityExcludeMode: f.priorityExcludeMode,
        // filterState.projects holds STRING ids — matchesProject() does
        // task.projectId.toString(). Numbers here would silently match nothing.
        projects: new Set(f.projects.map(String)),
        projectExcludeMode: f.projectExcludeMode,
        tags: new Set(f.tags),
        tagExcludeMode: f.tagExcludeMode,
        tagAndMode: f.tagAndMode,
        datePresets: new Set(f.datePresets),
        dateFrom: f.dateFrom,
        dateTo: f.dateTo,
        dateField: f.dateField,
        createdFrom: f.createdFrom,
        createdTo: f.createdTo,
        updatedFrom: f.updatedFrom,
        updatedTo: f.updatedTo,
        completedFrom: f.completedFrom,
        completedTo: f.completedTo,
        linkTypes: new Set(f.linkTypes),
        duplicateTitles: f.duplicateTitles,
        // "Top N most recently modified/created" — recency ranking + result cap,
        // applied by getFilteredTasks so the live list matches the review preview.
        kaiSort: f.sort || '',
        kaiLimit: f.limit && f.limit > 0 ? f.limit : null,
        // A Kai query is authoritative: a stale focused-task filter from an
        // earlier "view generated tasks" link would silently intersect with it.
        taskIds: new Set(),
        taskIdsSource: ''
    };
}

/**
 * Build the projectFilterState a KaiQuery describes.
 * Mirrors app.js projectFilterState exactly.
 */
export function kaiQueryToProjectFilterState(query) {
    const q = normalizeKaiQuery(query);
    const f = q.filters;

    return {
        search: f.search,
        statuses: new Set(f.projectStatuses),
        statusExcludeMode: f.projectStatusExcludeMode,
        taskFilter: f.taskFilter,
        updatedFilter: f.updatedFilter,
        tags: new Set(f.tags),
        tagExcludeMode: f.tagExcludeMode,
        projectIds: new Set(),
        completionMin: f.completionMin,
        completionMax: f.completionMax
    };
}

/**
 * Read the toolbar back into a KaiQuery, so "Refine" starts from whatever the
 * user is actually looking at — including filters they set by hand after Kai
 * applied its own.
 */
export function filterStateToKaiQuery(filterState, { entity = 'tasks', scope = 'active', view = '' } = {}) {
    const query = createEmptyKaiQuery(entity);
    query.scope = scope;
    query.view = view;
    if (!filterState) return query;

    const toArray = (value) => (value instanceof Set ? [...value] : Array.isArray(value) ? [...value] : []);
    const f = query.filters;

    if (entity === 'projects') {
        f.search = filterState.search || '';
        f.projectStatuses = toArray(filterState.statuses);
        f.projectStatusExcludeMode = !!filterState.statusExcludeMode;
        f.taskFilter = filterState.taskFilter || '';
        f.updatedFilter = filterState.updatedFilter || 'all';
        f.tags = toArray(filterState.tags);
        f.tagExcludeMode = !!filterState.tagExcludeMode;
        return query;
    }

    f.search = filterState.search || '';
    f.statuses = toArray(filterState.statuses);
    f.statusExcludeMode = !!filterState.statusExcludeMode;
    f.priorities = toArray(filterState.priorities);
    f.priorityExcludeMode = !!filterState.priorityExcludeMode;
    f.projects = toArray(filterState.projects).map(String);
    f.projectExcludeMode = !!filterState.projectExcludeMode;
    f.tags = toArray(filterState.tags);
    f.tagExcludeMode = !!filterState.tagExcludeMode;
    f.tagAndMode = !!filterState.tagAndMode;
    f.datePresets = toArray(filterState.datePresets);
    f.dateFrom = filterState.dateFrom || '';
    f.dateTo = filterState.dateTo || '';
    f.dateField = filterState.dateField || 'endDate';
    f.createdFrom = filterState.createdFrom || '';
    f.createdTo = filterState.createdTo || '';
    f.updatedFrom = filterState.updatedFrom || '';
    f.updatedTo = filterState.updatedTo || '';
    f.completedFrom = filterState.completedFrom || '';
    f.completedTo = filterState.completedTo || '';
    f.linkTypes = toArray(filterState.linkTypes);
    f.duplicateTitles = !!filterState.duplicateTitles;
    // Keep the recency ranking + cap when refining, so "Refine" starts from what
    // the user is actually looking at.
    f.sort = filterState.kaiSort || '';
    f.limit = filterState.kaiLimit && filterState.kaiLimit > 0 ? filterState.kaiLimit : null;
    return query;
}

/**
 * Describe a query as display chips: [{ dimension, values[], exclude }].
 *
 * Returns raw values, not copy — app.js localizes them. Keeping this pure means
 * it can be unit-tested without an i18n table.
 */
/** "2026-03-01 → 2026-03-31", or an open-ended bound. */
function formatRange(from, to) {
    if (from && to) return from === to ? from : `${from} → ${to}`;
    if (from) return `≥ ${from}`;
    if (to) return `≤ ${to}`;
    return '';
}

export function describeKaiQuery(query, vocabulary = {}) {
    const q = normalizeKaiQuery(query);
    const f = q.filters;
    const chips = [];
    const projectName = (id) => {
        if (id === KAI_NONE) return KAI_NONE;
        const match = (vocabulary.projects || []).find(project => String(project.id) === String(id));
        return match ? match.name : id;
    };

    if (f.search) chips.push({ dimension: 'search', values: [f.search], exclude: false });

    if (q.entity === 'projects') {
        if (f.projectStatuses.length) chips.push({ dimension: 'projectStatus', values: f.projectStatuses, exclude: f.projectStatusExcludeMode });
        if (f.taskFilter) chips.push({ dimension: 'taskFilter', values: [f.taskFilter], exclude: false });
        if (f.completionMin != null || f.completionMax != null) {
            const label = f.completionMin != null && f.completionMax != null
                ? (f.completionMin === f.completionMax ? `${f.completionMin}%` : `${f.completionMin}–${f.completionMax}%`)
                : (f.completionMin != null ? `≥ ${f.completionMin}%` : `≤ ${f.completionMax}%`);
            chips.push({ dimension: 'completion', values: [label], exclude: false });
        }
    } else {
        if (f.statuses.length) chips.push({ dimension: 'status', values: f.statuses, exclude: f.statusExcludeMode });
        if (f.priorities.length) chips.push({ dimension: 'priority', values: f.priorities, exclude: f.priorityExcludeMode });
        if (f.projects.length) chips.push({ dimension: 'project', values: f.projects.map(projectName), exclude: f.projectExcludeMode });
        if (f.datePresets.length) chips.push({ dimension: 'datePreset', values: f.datePresets, exclude: false });
        if (f.dateFrom || f.dateTo) chips.push({ dimension: 'dateRange', values: [formatRange(f.dateFrom, f.dateTo)], exclude: false });
        if (f.createdFrom || f.createdTo) chips.push({ dimension: 'created', values: [formatRange(f.createdFrom, f.createdTo)], exclude: false });
        if (f.updatedFrom || f.updatedTo) chips.push({ dimension: 'updatedRange', values: [formatRange(f.updatedFrom, f.updatedTo)], exclude: false });
        if (f.completedFrom || f.completedTo) chips.push({ dimension: 'completed', values: [formatRange(f.completedFrom, f.completedTo)], exclude: false });
        if (f.linkTypes.length) chips.push({ dimension: 'linkType', values: f.linkTypes, exclude: false });
        if (f.duplicateTitles) chips.push({ dimension: 'duplicate', values: ['duplicate'], exclude: false });
    }

    if (f.tags.length) {
        chips.push({ dimension: 'tag', values: f.tags, exclude: f.tagExcludeMode, and: f.tagAndMode });
    }
    if (f.updatedFilter && f.updatedFilter !== 'all') {
        chips.push({ dimension: 'updated', values: [f.updatedFilter], exclude: false });
    }
    // Recency ordering + result cap ("the 5 most recently modified"). These are a
    // real, applied interpretation — without a chip the review reads as "no filter".
    if (f.sort) {
        chips.push({ dimension: 'recency', values: [f.sort], exclude: false });
    }
    if (f.limit) {
        chips.push({ dimension: 'limit', values: [String(f.limit)], exclude: false });
    }
    if (q.scope === 'archived') {
        chips.push({ dimension: 'scope', values: ['archived'], exclude: false });
    }

    // Sort the values inside each chip so "Tag: franky, nami" reads the same way
    // every time, regardless of the order the parser happened to find them in.
    // The chips themselves are ordered by the caller, which knows the localized
    // labels; a date range is left alone because its single value is a span.
    const singleSpanDimensions = new Set(['dateRange', 'created', 'updatedRange', 'completed', 'search', 'completion']);
    return chips.map(chip => (
        singleSpanDimensions.has(chip.dimension)
            ? chip
            : { ...chip, values: [...chip.values].sort((a, b) => String(a).localeCompare(String(b))) }
    ));
}

/**
 * Why did this match nothing?
 *
 * Re-runs the real filter with each dimension dropped in turn, so we can say
 * "dropping Priority: High would match 7" instead of showing an empty board.
 * Cheap: at most ~8 extra passes over an in-memory array.
 *
 * Never broadens the search on its own — it only reports.
 *
 * @param {Function} filterFn - (filterStateLike) => matched[] (inject filterTasks)
 * @returns {Array<{dimension: string, count: number}>} sorted best-first
 */
export function explainEmptyKaiResult(query, filterFn) {
    const q = normalizeKaiQuery(query);
    const dimensions = [
        ['status', ['statuses', 'statusExcludeMode']],
        ['priority', ['priorities', 'priorityExcludeMode']],
        ['project', ['projects', 'projectExcludeMode']],
        ['tag', ['tags', 'tagExcludeMode', 'tagAndMode']],
        ['datePreset', ['datePresets']],
        ['dateRange', ['dateFrom', 'dateTo']],
        ['created', ['createdFrom', 'createdTo']],
        ['updatedRange', ['updatedFrom', 'updatedTo']],
        ['completed', ['completedFrom', 'completedTo']],
        ['linkType', ['linkTypes']],
        ['duplicate', ['duplicateTitles']],
        ['search', ['search']]
    ];

    const suggestions = [];
    for (const [name, keys] of dimensions) {
        const relaxed = normalizeKaiQuery(q);
        let touched = false;
        const empty = createEmptyKaiQuery(q.entity).filters;
        for (const key of keys) {
            const current = relaxed.filters[key];
            const isSet = Array.isArray(current) ? current.length > 0 : current !== empty[key];
            if (isSet) touched = true;
            relaxed.filters[key] = empty[key];
        }
        if (!touched) continue;
        const count = filterFn(kaiQueryToFilterState(relaxed)).length;
        if (count > 0) suggestions.push({ dimension: name, count });
    }

    return suggestions.sort((a, b) => b.count - a.count);
}

/** True when the query has at least one real filter to apply. */
export function hasApplicableFilters(query) {
    return countKaiQueryFilters(query) > 0 || normalizeKaiQuery(query).scope === 'archived';
}
